import { useCallback, useEffect, useState } from 'react';

import { AppBar, Box, Fab, List, Toolbar, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { useModal } from 'react-modal-hook';

import AppliedFilters from 'components/AppliedFilters';
import FilterButton from 'components/FilterButton';
import LoadingOverlay from 'components/LoadingOverlay';
import ModalAddContato from 'components/ModalAddContato';
import SearchBar from 'components/SearchBar';
import VirtualizedListContainer from 'components/VirtualizedListContainer';
import * as socketEvents from 'constants/socketEvents';
import useFilters from 'hooks/useFilters';
import { useVirtualizedList } from 'hooks/useVirtualizedList';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atualizarContatoStatus,
  atualizarFotoContato,
  contatosSelector,
  ContatoStatusTypes,
  fetchContatosV2,
  resetContatosV2,
} from 'store/reducers/contatos';
import { fetchTags, TagInterface } from 'store/reducers/tags';
import { IFiltersOptions } from 'types';

import ContatoListItem from './components/ContatoListItem';
import ModalFilters from './components/ModalFilters';

// import ModalCorrigirContato from './components/ModalCorrigirContato';

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  list: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
  },
}));

export default function Contatos() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const {
    contatosV2: contatos,
    contatosV2InfoPage,
    loadingContatosV2: loadingContatos,
    loadingRemoverContato,
  } = useAppSelector(contatosSelector);
  const [filter, setFilter] = useState('');
  const [selectedActionContatoId, setSelectedActionContatoId] =
    useState<string>();

  const { filters, setFilters, onRemoveAllFilters, onRemoveFilter } =
    useFilters();

  const [selectedBlockedStatus, setSelectedBlockedStatus] = useState<
    'BLOCKED' | 'UNBLOCKED' | null
  >(null);
  const [selectedTags, setSelectedTags] = useState<TagInterface[]>([]);

  const hasMore = contatos.length < contatosV2InfoPage.total;

  const getContatos = useCallback(
    (params?: {
      page?: number;
      search?: string;
      tagIds?: string[];
      blockStatus?: string;
    }) => {
      dispatch(fetchContatosV2(params));
    },
    [dispatch],
  );

  const resetAllFilters = useCallback(() => {
    setFilter('');
  }, []);

  const onRefresh = useCallback(() => {
    dispatch(resetContatosV2());
    resetAllFilters();
    getContatos({ page: 1 });
  }, [dispatch, getContatos, resetAllFilters]);

  const onLoadNextContatos = useCallback(() => {
    if (!hasMore || loadingContatos) return;
    const nextPage = contatosV2InfoPage.page + 1;

    const tagIds = filters.find(f => f.property === 'tagIds')?.value as
      | string[]
      | undefined;
    const blockStatus = filters.find(f => f.property === 'blockStatus')
      ?.value as string | undefined;

    getContatos({
      page: nextPage,
      search: filter || undefined,
      tagIds,
      blockStatus,
    });
  }, [
    filter,
    filters,
    contatosV2InfoPage,
    hasMore,
    loadingContatos,
    getContatos,
  ]);

  const { parentRef, virtualItems, rowVirtualizer } = useVirtualizedList({
    items: contatos,
    hasMore,
    loading: loadingContatos,
    estimateSize: 85,
    next: onLoadNextContatos,
  });

  const [showModalAdd, hideModalAdd] = useModal(
    ({ in: open }) => (
      <ModalAddContato
        open={open}
        onClose={hideModalAdd}
        onSuccess={() => {
          setFilter('');
          onRefresh();
        }}
      />
    ),
    [],
  );

  // const [showModalCorrigirContato, hideModalCorrigirContato] = useModal(
  //   ({ in: open }) => (
  //     <ModalCorrigirContato
  //       open={open}
  //       contato={contatoSelected}
  //       onClose={() => {
  //         setContatoSelected(undefined);
  //         hideModalCorrigirContato();
  //       }}
  //       onSuccess={() => {
  //         onRefresh();
  //         setContatoSelected(undefined);
  //       }}
  //     />
  //   ),
  //   [contatoSelected],
  // );

  const handleSearch = (value: string) => {
    setFilter(value);
    dispatch(resetContatosV2());

    const tagIds = filters.find(f => f.property === 'tagIds')?.value as
      | string[]
      | undefined;
    const blockStatus = filters.find(f => f.property === 'blockStatus')
      ?.value as string | undefined;

    getContatos({
      page: 1,
      search: value || undefined,
      tagIds,
      blockStatus,
    });
  };

  // const handleCorrigir = (contato: ContatoInterface) => {
  //   setContatoSelected(contato);
  //   showModalCorrigirContato();
  // };

  const handleFilterTags = (newFilters: IFiltersOptions[]) => {
    setFilters(newFilters);
    dispatch(resetContatosV2());

    const tagIds = newFilters.find(f => f.property === 'tagIds')?.value as
      | string[]
      | undefined;
    const blockStatus = newFilters.find(f => f.property === 'blockStatus')
      ?.value as string | undefined;

    getContatos({
      page: 1,
      search: filter || undefined,
      tagIds,
      blockStatus,
    });
  };

  const handleRemoveFilter = (filterProperty: IFiltersOptions['property']) => {
    // When removed from AppliedFilters, also clear specific local states
    if (filterProperty === 'tagIds') {
      setSelectedTags([]);
    }
    if (filterProperty === 'blockStatus') {
      setSelectedBlockedStatus(null);
    }
    onRemoveFilter(filterProperty);

    // Re-fetch with remaining filters
    const remainingFilters = filters.filter(f => f.property !== filterProperty);

    const tagIds = remainingFilters.find(f => f.property === 'tagIds')
      ?.value as string[] | undefined;
    const blockStatus = remainingFilters.find(f => f.property === 'blockStatus')
      ?.value as string | undefined;

    dispatch(resetContatosV2());
    getContatos({
      page: 1,
      search: filter || undefined,
      tagIds,
      blockStatus,
    });
  };

  const handleRemoveAllFilters = () => {
    setSelectedTags([]);
    setSelectedBlockedStatus(null);
    onRemoveAllFilters();
    dispatch(resetContatosV2());
    getContatos({ page: 1 });
  };

  const [showModalFilters, hideModalFilters] = useModal(
    ({ in: open }) => (
      <ModalFilters
        open={open}
        onClose={hideModalFilters}
        onFilter={handleFilterTags}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
        selectedBlockedStatus={selectedBlockedStatus}
        onBlockedStatusChange={setSelectedBlockedStatus}
      />
    ),
    [selectedTags, selectedBlockedStatus, handleFilterTags],
  );

  useEffect(() => {
    dispatch(resetContatosV2());
    getContatos({ page: 1 });
    dispatch(fetchTags());

    return () => {
      setFilter('');
      dispatch(resetContatosV2());
    };
  }, [dispatch, getContatos]);

  useEffect(() => {
    // Observa a mudança de status do contato adicionado
    const handlerContatoStatus = (data: {
      contatoId: string;
      status: ContatoStatusTypes;
    }) => {
      dispatch(atualizarContatoStatus(data));
    };
    // Observa quando a foto do contato foi alterada
    const handlerContatoFoto = (data: { contatoId: string; foto: string }) => {
      dispatch(atualizarFotoContato(data));
    };

    socket.on(socketEvents.ON_CONTATO_STATUS, handlerContatoStatus);
    socket.on(socketEvents.ON_FOTO_CONTATO_ATUALIZADA, handlerContatoFoto);

    return () => {
      socket.off(socketEvents.ON_CONTATO_STATUS, handlerContatoStatus);
      socket.off(socketEvents.ON_FOTO_CONTATO_ATUALIZADA, handlerContatoFoto);
    };
  }, [dispatch]);

  const renderContatos = virtualItems.map(virtualRow => {
    const contato = contatos[virtualRow.index];

    if (!contato) return null;

    return (
      <ContatoListItem
        contato={contato}
        selected={selectedActionContatoId === contato.id}
        onToggleActionSelection={() =>
          setSelectedActionContatoId(currentId =>
            currentId === contato.id ? undefined : contato.id,
          )
        }
        rowVirtualizer={rowVirtualizer}
        virtualRow={virtualRow}
        onRefresh={onRefresh}
        key={contato.id}
      />
    );
  });

  return (
    <Box className={classes.root}>
      <AppBar position="relative" color="transparent" elevation={0}>
        <Toolbar style={{ minHeight: 48 }} disableGutters>
          <Typography variant="h6">Contatos</Typography>

          <Box flexGrow={1} ml={2} mr={2}>
            <Fab size="small" color="primary" onClick={showModalAdd}>
              <AddIcon />
            </Fab>
          </Box>

          <Box display="flex" alignItems="center" gridGap={8}>
            <Box flex={1} minWidth={280}>
              <SearchBar
                value={filter}
                onChange={handleSearch}
                onCancelSearch={() => {
                  dispatch(resetContatosV2());
                  setFilter('');
                  getContatos({ page: 1 });
                }}
              />
            </Box>

            <FilterButton onClick={showModalFilters} />
          </Box>
        </Toolbar>
      </AppBar>

      {filters.length > 0 && (
        <AppliedFilters
          filters={filters}
          onRemove={handleRemoveFilter}
          onRemoveAll={handleRemoveAllFilters}
        />
      )}

      <List id="listContatos" className={classes.list} ref={parentRef}>
        <VirtualizedListContainer
          virtualItems={virtualItems}
          virtualizer={rowVirtualizer}
          loading={loadingContatos}
          empty={
            filter
              ? {
                  title: 'Tem certeza que digitou certo !?',
                  description: `Não encontramos ninguém chamado "${filter}"`,
                }
              : {
                  title: 'Ainda não há uma lista de contatos!',
                  description:
                    '"Adicione seus novos contatos e eles aparecerão aqui"',
                }
          }
        >
          {renderContatos}
        </VirtualizedListContainer>
      </List>

      {loadingRemoverContato && <LoadingOverlay />}
    </Box>
  );
}
